import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardBody, Col, Container, Input, Label, Row, Button, Alert, Spinner, Form } from 'reactstrap';
import MainContext from '../../../Components/Contexts/MainContext';
import { LanguageContext } from '../../../Components/Contexts/LanguageContext';
import LanguageDropdown from '../../../Components/Common/LanguageDropdown';
import { applyMyLanguage } from '../../../services/kullaniciDilService';
import { girisSorgusu } from '../../../services/mfaService';
import MfaGirisAdimi from './MfaGirisAdimi';
import logoLight from "../../../assets/images/logo-light.png";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation("giris");
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [passwordShow, setPasswordShow] = useState(false);
    // #640: set when the password was right and the account has a second factor: what the code screen needs (the login ticket lives here only).
    const [mfa, setMfa] = useState(null);
    const navigate = useNavigate();

    const { handleLoginContext, handleMfaLoginContext } = useContext(MainContext);
    // The login page has no header, so it carries its own TR/EN switch (same dropdown as the header).
    const { selectedLang, setSelectedLang } = useContext(LanguageContext);

    // `t` changes identity with the language, so the tab title follows the switch.
    useEffect(() => { document.title = t("homeLogin.girisYapFds"); }, [t]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // WAS (until #640): await handleLoginContext(username, password);
            const sonuc = await handleLoginContext(username, password);
            if (sonuc && sonuc.mfaRequired) {
                setPassword('');
                setMfa(girisSorgusu(sonuc.challenge));
                return;
            }
            // #562: the user's own language (their saved choice, else English for a foreign user) replaces the browser's last one,
            // before the first page renders. A failed read keeps the language as it is.
            await applyMyLanguage(setSelectedLang);
            navigate('/Dashboard');
        } catch (err) {
            setError(err.message || 'An error occurred during login');
        } finally {
            setLoading(false);
        }
    };


    // #640: the second step finished: the ticket became a session; the rest is what a password login does.
    const mfaBasari = async (userId, token) => {
        await handleMfaLoginContext(userId, token);
        await applyMyLanguage(setSelectedLang);
        navigate('/Dashboard');
    };

    // #640: back to the password screen (the ticket is gone, or the user chose to), with the reason when there is one.
    const mfaGeri = (hataAnahtari) => {
        setMfa(null);
        setError(hataAnahtari ? t(hataAnahtari) : null);
    };

    return (
        <React.Fragment>
            <div className="auth-page-wrapper pt-5">
                <div className="auth-one-bg-position auth-one-bg" id="auth-particles">
                    <div className="bg-overlay"></div>
                    <div className="shape">
                        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 1440 120">
                            <path d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"></path>
                        </svg>
                    </div>

                    <div className="position-absolute top-0 end-0 p-3" style={{ zIndex: 10 }}>
                        <LanguageDropdown selectedLang={selectedLang} setSelectedLang={setSelectedLang} />
                    </div>

                    <div className="auth-page-content mt-lg-5">
                        <Container>
                            <Row>
                                <Col lg={12}>
                                    <div className="text-center mt-sm-5 mb-4 text-white-50">
                                        <div>
                                            <Link to="/" className="d-inline-block auth-logo">
                                                <img src={logoLight} alt="" height="100" />
                                            </Link>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="justify-content-center">
                                <Col md={8} lg={6} xl={5}>
                                    <Card className="mt-4 card-bg-fill">
                                        <CardBody className="p-4">
                                            {mfa ? (
                                                <MfaGirisAdimi sorgu={mfa} onBasari={mfaBasari} onGeri={mfaGeri} />
                                            ) : (
                                            <>
                                            <div className="text-center mt-2">
                                                <h5 className="text-body">{t("homeLogin.tekrarHosgeldiniz")}</h5>
                                                <p className="text-muted">{t("homeLogin.fuarDenetimSistemineDevamEtmek")}</p>
                                            </div>
                                            {error && <Alert color="danger">{error}</Alert>}
                                            <div className="p-2">
                                                <Form onSubmit={handleLogin}>
                                                    <div className="mb-3">
                                                        <Label htmlFor="username" className="form-label">{t("homeLogin.kullaniciAdi")}</Label>
                                                        <Input
                                                            name="username"
                                                            className="form-control"
                                                            placeholder={t("homeLogin.kullaniciAdi")}
                                                            type="text"
                                                            value={username}
                                                            onChange={(e) => setUsername(e.target.value)}
                                                            invalid={!!error}
                                                        />
                                                    </div>

                                                    <div className="mb-3">
                                                        <div className="float-end">
                                                            <Link to="/sifremi-unuttum" className="text-muted">{t("homeLogin.sifremiUnuttum")}</Link>
                                                        </div>
                                                        <Label className="form-label" htmlFor="password-input">{t("homeLogin.sifre")}</Label>
                                                        <div className="position-relative auth-pass-inputgroup mb-3">
                                                            <Input
                                                                name="password"
                                                                value={password}
                                                                type={passwordShow ? "text" : "password"}
                                                                className="form-control pe-5"
                                                                placeholder={t("homeLogin.sifre")}
                                                                onChange={(e) => setPassword(e.target.value)}
                                                                invalid={!!error}
                                                            />
                                                            <button className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted" type="button" id="password-addon" onClick={() => setPasswordShow(!passwordShow)}><i className="ri-eye-fill align-middle"></i></button>
                                                        </div>
                                                    </div>

                                                    <div className="form-check">
                                                        <Input className="form-check-input" type="checkbox" value="" id="auth-remember-check" />
                                                        <Label className="form-check-label" htmlFor="auth-remember-check">{t("homeLogin.beniHatirla")}</Label>
                                                    </div>

                                                    <div className="mt-4">
                                                        <Button color="primary" disabled={loading} className="btn btn-primary w-100" type="submit">
                                                            {loading ? <Spinner size="sm" className='me-2'>{" "}{t("common:yukleniyor")}{" "}</Spinner> : t("homeLogin.girisYap")}
                                                        </Button>
                                                    </div>
                                                </Form>
                                            </div>
                                            </>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Login;
